import { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Dialog } from "@/components/ui/dialog";
import { CreateAppDialog } from "./CreateAppDialog";
import { BulkAppImportDialog } from "./BulkAppImportDialog";
import { AppCatalogTable } from "./AppCatalogTable";
import { AppSearchInput } from "./AppSearchInput";
import { AppFilters, FilterState } from "./AppFilters";
import { AppDetailModal } from "./AppDetailModal";
import { Button } from "@/components/ui/button";
import { Plus, Upload, X, CheckCircle2 } from "lucide-react";
import { App } from "@/types/app";
import { useAppCategories, logAppCategories } from "@/utils/appCategoriesUtils";

export const AppsSection = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState("");
  const [filters, setFilters] = useState<FilterState>({
    category: "all",
    systemAppFilter: "all",
    tags: []
  });
  const [selectedApp, setSelectedApp] = useState<App | null>(null);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isBulkImportDialogOpen, setIsBulkImportDialogOpen] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  // Temporary: Log all app categories to console
  const { categories, isLoading: categoriesLoading } = useAppCategories();
  useEffect(() => {
    if (!categoriesLoading && categories.length > 0) {
      logAppCategories(categories);
    }
  }, [categories, categoriesLoading]);

  const selectedAppId = searchParams.get('appId');
  useEffect(() => {
    if (!selectedAppId) setSelectedApp(null);
  }, [selectedAppId]);

  const handleAppCreated = () => {
    setRefreshKey(prev => prev + 1);
    setIsCreateDialogOpen(false);
  };

  const handleBulkImportComplete = () => {
    setRefreshKey(prev => prev + 1);
    setIsBulkImportDialogOpen(false);
  };

  const handleClearFilters = () => {
    setFilters({ category: "all", systemAppFilter: "all", tags: [] });
  };

  const handleAppSelect = (app: App | null) => {
    const newParams = new URLSearchParams(searchParams);
    if (app) {
      newParams.set('appId', app.id);
      setSelectedApp(app);
    } else {
      newParams.delete('appId');
      newParams.delete('appTab');
      setSelectedApp(null);
    }
    setSearchParams(newParams);
  };

  const handleDeleteAppFromSidebar = () => {
    const newParams = new URLSearchParams(searchParams);
    newParams.delete('appId');
    newParams.delete('appTab');
    setSearchParams(newParams);
    setRefreshKey(prev => prev + 1);
  };

  return (
    <div className="flex flex-col h-screen overflow-hidden">
      {/* Sticky header: always visible */}
      <div className="sticky top-0 z-30 bg-white px-6 py-4">
        <div className="flex items-center gap-3">
          <div className="flex-1">
            <AppSearchInput
              searchQuery={searchTerm}
              onSearchChange={setSearchTerm}
              onCancel={() => setSearchTerm("")}
              placeholder="Search apps..."
            />
          </div>
          <div className="flex items-center gap-2">
            <AppFilters
              filters={filters}
              onFiltersChange={setFilters}
              onClearFilters={handleClearFilters}
            />
            <Button
              onClick={() => navigate("/section/verify-apps")}
              variant="outline"
              size="sm"
              className="flex items-center gap-1 text-xs h-8"
            >
              <CheckCircle2 className="w-3 h-3" />
              Verify Apps
            </Button>
            <Button
              onClick={() => setIsBulkImportDialogOpen(true)}
              variant="outline"
              size="sm"
              className="flex items-center gap-1 text-xs h-8"
            >
              <Upload className="w-3 h-3" />
              Import
            </Button>
            <Button
              onClick={() => setIsCreateDialogOpen(true)}
              size="sm"
              className="flex items-center gap-1 text-xs h-8 px-6"
            >
              <Plus className="w-3 h-3" />
              Add
            </Button>
          </div>
        </div>
      </div>

      {/* Scrollable content: table + sidebar */}
      <div className="flex flex-1 overflow-y-auto">
        <div className={`transition-all duration-300 ${selectedApp ? 'w-1/2' : 'w-full'} px-6 flex flex-col`}>
          <AppCatalogTable
            key={refreshKey}
            searchTerm={searchTerm}
            selectedCategory={filters.category}
            systemAppFilter={filters.systemAppFilter}
            selectedTags={filters.tags}
            onAppSelect={handleAppSelect}
            selectedApp={selectedApp}
          />
        </div>

        {selectedApp && (
          <div className="w-1/2 border-l border-gray-200 bg-white flex flex-col">
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold">{selectedApp.app_name}</h3>
              <Button variant="ghost" size="sm" onClick={() => handleAppSelect(null)}>
                <X className="w-4 h-4" />
              </Button>
            </div>
            <div className="flex-1 overflow-hidden">
              <AppDetailModal
                app={selectedApp}
                onClose={() => handleAppSelect(null)}
                onDelete={handleDeleteAppFromSidebar}
                isDeleting={false}
              />
            </div>
          </div>
        )}
      </div>

      {/* Dialogs */}
      <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
        <CreateAppDialog onClose={() => setIsCreateDialogOpen(false)} onAppCreated={handleAppCreated} />
      </Dialog>
      <Dialog open={isBulkImportDialogOpen} onOpenChange={setIsBulkImportDialogOpen}>
        <BulkAppImportDialog onClose={() => setIsBulkImportDialogOpen(false)} onImportComplete={handleBulkImportComplete} />
      </Dialog>
    </div>
  );
};
